// Décodeur GRTC v1, voir docs/binary-trees/FORMAT.md.
// Tampons alignés sur 8 octets et little-endian côté API, sinon les vues typées échouent.

const MAGIC = 'GRTC';

export const SUPPORTED_FORMAT_VERSION = 1;

// magic(4) + version(2) + réservé(2) + schemaLen(4) + dataStart(4)
const HEADER_LENGTH = 16;

export class UnsupportedTreeFormatError extends Error {
    constructor(message) {
        super(message);
        this.name = 'UnsupportedTreeFormatError';
    }
}

const decoder = new TextDecoder();

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

export default class TreeChunk {
    constructor(buffer) {
        if (!(buffer instanceof ArrayBuffer))
            throw new UnsupportedTreeFormatError("Le bloc reçu n'est pas un ArrayBuffer.");
        if (buffer.byteLength < HEADER_LENGTH)
            throw new UnsupportedTreeFormatError(`Bloc trop court (${buffer.byteLength} octets).`);

        this.buffer = buffer;

        const header = new DataView(buffer);

        const magic = String.fromCharCode(header.getUint8(0), header.getUint8(1), header.getUint8(2), header.getUint8(3));
        if (magic !== MAGIC)
            throw new UnsupportedTreeFormatError(`Signature inattendue « ${magic} » (attendu « ${MAGIC} »).`);

        // true = little-endian (DataView lit en big-endian par défaut).
        this.version = header.getUint16(4, true);
        if (this.version !== SUPPORTED_FORMAT_VERSION)
            throw new UnsupportedTreeFormatError(
                `Version de format ${this.version} inconnue (ce client sait lire la version ${SUPPORTED_FORMAT_VERSION}). `
                + `Back et front sont probablement désynchronisés.`
            );

        const schemaLength = header.getUint32(8, true);
        this.dataStart = header.getUint32(12, true);

        if (HEADER_LENGTH + schemaLength > buffer.byteLength || this.dataStart > buffer.byteLength)
            throw new UnsupportedTreeFormatError('En-tête incohérent : le bloc est probablement tronqué.');

        this.schema = JSON.parse(decoder.decode(new Uint8Array(buffer, HEADER_LENGTH, schemaLength)));
        this.rowCount = this.schema.rowCount;
        this.missingCustomFieldIds = this.schema.missingCustomFieldIds || [];

        this.columns = new Map(this.schema.columns.map(column => [column.name, column]));
        this.views = new Map();
        this.dictionaries = new Map();
    }

    get byteLength() {
        return this.buffer.byteLength;
    }

    get columnNames() {
        return this.schema.columns.map(column => column.name);
    }

    has(name) {
        return this.columns.has(name);
    }

    column(name) {
        const column = this.columns.get(name);
        if (!column) throw new UnsupportedTreeFormatError(`Colonne « ${name} » absente du bloc.`);
        return column;
    }

    view(name, role, Type, size) {
        const key = `${name}#${role}`;
        const cached = this.views.get(key);
        if (cached) return cached;

        const buffers = this.column(name).buffers;
        const descriptor = buffers[role];
        if (!descriptor) throw new UnsupportedTreeFormatError(`Tampon « ${role} » absent de la colonne « ${name} ».`);

        const [offset, length] = descriptor;
        const created = new Type(this.buffer, this.dataStart + offset, length / size);
        this.views.set(key, created);
        return created;
    }

    int32(name) { return this.view(name, 'values', Int32Array, 4); }
    float32(name) { return this.view(name, 'values', Float32Array, 4); }
    float64(name) { return this.view(name, 'values', Float64Array, 8); }
    uint8(name) { return this.view(name, 'values', Uint8Array, 1); }

    flagReader(name, bitName) {
        const values = this.uint8(name);
        const position = (this.column(name).bits || []).indexOf(bitName);
        if (position < 0) throw new UnsupportedTreeFormatError(`Drapeau « ${bitName} » absent de la colonne « ${name} ».`);
        const mask = 1 << position;
        return row => (values[row] & mask) !== 0;
    }

    dictionary(name) {
        const cached = this.dictionaries.get(name);
        if (cached) return cached;

        const offsets = this.view(name, 'dictOffsets', Int32Array, 4);
        const data = this.view(name, 'dictData', Uint8Array, 1);
        const entries = new Array(offsets.length - 1);
        for (let k = 0; k < entries.length; k++) {
            const start = offsets[k], end = offsets[k + 1];
            entries[k] = end > start ? decoder.decode(data.subarray(start, end)) : null;
        }
        this.dictionaries.set(name, entries);
        return entries;
    }

    dictReader(name) {
        const indexes = this.int32(name);
        const entries = this.dictionary(name);
        return row => {
            const index = indexes[row];
            return index < 0 ? null : entries[index];
        };
    }

    // Chaîne vide et chaîne absente sont indistinguables : null dans les deux cas.
    utf8Reader(name) {
        const offsets = this.view(name, 'offsets', Int32Array, 4);
        const data = this.view(name, 'data', Uint8Array, 1);
        return row => {
            const start = offsets[row], end = offsets[row + 1];
            return end > start ? decoder.decode(data.subarray(start, end)) : null;
        };
    }

    guidReader(name) {
        const bytes = this.uint8(name);
        return row => guidAt(bytes, row * 16);
    }

    listInt32(name) {
        return {
            offsets: this.view(name, 'offsets', Int32Array, 4),
            values: this.view(name, 'values', Int32Array, 4)
        };
    }

    listDict(name) {
        return {
            offsets: this.view(name, 'offsets', Int32Array, 4),
            values: this.view(name, 'values', Int32Array, 4),
            entries: this.dictionary(name)
        };
    }

    listStruct(name) {
        const column = this.column(name);
        return {
            offsets: this.view(name, 'offsets', Int32Array, 4),
            id: this.view(name, 'id', Uint8Array, 1),
            height: this.view(name, 'height', Float32Array, 4),
            circumference: this.view(name, 'circumference', Float32Array, 4),
            crownDiameter: this.view(name, 'crownDiameter', Float32Array, 4),
            fields: column.fields || []
        };
    }

    listJson(name) {
        const offsets = this.view(name, 'offsets', Int32Array, 4);
        const innerOffsets = this.view(name, 'innerOffsets', Int32Array, 4);
        const data = this.view(name, 'data', Uint8Array, 1);
        return {
            offsets,
            read: index => {
                const start = innerOffsets[index], end = innerOffsets[index + 1];
                return end > start ? JSON.parse(decoder.decode(data.subarray(start, end))) : null;
            }
        };
    }

    columnSizes() {
        return this.schema.columns
            .map(column => ({
                name: column.name,
                kind: column.kind,
                bytes: Object.values(column.buffers).reduce((total, [, length]) => total + length, 0)
            }))
            .sort((a, b) => b.bytes - a.bytes)
            .map(column => ({ ...column, bytesPerTree: Math.round(column.bytes / this.rowCount) }));
    }
}

// NaN = mesure absente (0 est une mesure valide).
export function measure(value) {
    return Number.isNaN(value) ? null : value;
}

// Ordre de `Guid.ToByteArray()` (.NET) : à garder synchrone avec l'encodeur.
export function guidAt(bytes, offset) {
    return (
        HEX[bytes[offset + 3]] + HEX[bytes[offset + 2]] + HEX[bytes[offset + 1]] + HEX[bytes[offset]] + '-' +
        HEX[bytes[offset + 5]] + HEX[bytes[offset + 4]] + '-' +
        HEX[bytes[offset + 7]] + HEX[bytes[offset + 6]] + '-' +
        HEX[bytes[offset + 8]] + HEX[bytes[offset + 9]] + '-' +
        HEX[bytes[offset + 10]] + HEX[bytes[offset + 11]] + HEX[bytes[offset + 12]] +
        HEX[bytes[offset + 13]] + HEX[bytes[offset + 14]] + HEX[bytes[offset + 15]]
    );
}
